import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    Stepper,
    Step,
    StepLabel,
    Box,
    Stack,
    IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import styled from "@emotion/styled";
import { useDispatch, useSelector } from "react-redux";
import { restoreAttachments } from "../../utils/uploadUtils";
import {
    createGroupTemplate,
    createProjectTemplate,
    createTaskTemplate,
    createChecklistTemplate,
    createQuoteTemplate,
    updateGroupTemplate,
    updateProjectTemplate,
    updateTaskTemplate,
    updateChecklistTemplate,
    updateQuoteTemplate,
    getTemplates,
    getTemplateImportSource,
    importTemplate,
} from "../../redux/ducks/template";
import { getAccounts, getAccountGroups, getAccountProjects } from "../../redux/ducks/account";
import { getDepartments } from "../../redux/ducks/department";
import { getSkills } from "../../redux/slices/skill";
import { showAlert } from "../../redux/slices/alerts";
import StepBasics from "./StepBasics";
import StepClassification from "./StepClassification";
import StepAssign from "./StepAssign";
import StepScheduling from "./StepScheduling";
import StepChecklist from "./StepChecklist";
import StepChecklistBasics from "./StepChecklistBasics";
import StepQuoteBasics from "./StepQuoteBasics";
import StepQuoteLineItems from "./StepQuoteLineItems";
import StepQuoteScope from "./StepQuoteScope";
import StepBilling from "./StepBilling";
import StepAttachments from "./StepAttachments";
import { formatDate } from "../../utils/dateUtils";

const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(3),
    },
}));

const StepContent = styled(Box)(({ theme }) => ({
    minHeight: 300,
    paddingTop: theme.spacing(3),
}));

const WizardActions = styled(Stack)(({ theme }) => ({
    marginTop: theme.spacing(3),
}));

function blankForm() {
    return {
        displayName: "",
        name: "",
        overview: "",
        description: "",
        priority: "Medium",
        privacy: "Public",
        billingType: "Parent",
        templateDateType: "specific",
        startDate: "",
        dueDate: "",
        templateStartOffset: 0,
        templateDueOffset: 0,
        templateStartRelative: "",
        templateDueRelative: "",
        status: "Pending",
        group: "",
        project: "",
        department: "",
        skill: "",
        readiness: "Ready",
        responsibleSlot: 0,
        checklist: [],
        budgeted: "",
        quoted: "",
        projectType: "",
        isActive: true,
        extraDays: 0,
        daysBeforeStart: 0,
        quoteLineItems: [],
        assumptions: [],
        inclusions: [],
        exclusions: [],
        tags: [],
    };
}

// taskTemplate.department is an array in the schema while group/project hold a single ref;
// the wizard edits one value for all three.
function firstRefId(value) {
    const ref = Array.isArray(value) ? value[0] : value;
    return ref?._id || ref || "";
}

// A checklist template carries its steps at the top level; every other type nests them under checklist.items.
function checklistItemsFrom(t) {
    const source = Array.isArray(t.items) ? t.items : Array.isArray(t.checklist?.items) ? t.checklist.items : [];
    return [...source].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((i) => ({ id: crypto.randomUUID(), name: i.name }));
}

// The scope editors key rows on a stable id, so plain strings from the API get one here and lose it again at submit.
function scopeItemsFrom(list) {
    return (Array.isArray(list) ? list : []).map((text) => ({ id: crypto.randomUUID(), name: text }));
}

function quoteLineItemsFrom(t) {
    return (Array.isArray(t.lineItems) ? t.lineItems : []).map((li) => ({
        id: crypto.randomUUID(),
        title: li.title || "",
        description: li.description || "",
        workType: li.workType?._id || li.workType || "",
        hours: li.hours ?? 0,
        isActionable: !!li.isActionable,
    }));
}

function formFromTemplate(t) {
    return {
        displayName: t.displayName || "",
        name: t.name || "",
        overview: t.overview || "",
        description: t.description || "",
        priority: t.priority || "Medium",
        privacy: t.privacy || "Public",
        billingType: t.billingType || t.billingTerms?.type || "Parent",
        templateDateType: t.templateDateType || "specific",
        startDate: formatDate(t.startDate),
        dueDate: formatDate(t.dueDate),
        templateStartOffset: t.templateStartOffset ?? 0,
        templateDueOffset: t.templateDueOffset ?? 0,
        templateStartRelative: t.templateStartRelative || "",
        templateDueRelative: t.templateDueRelative || "",
        status: t.status || "Pending",
        group: t.group?._id || t.group || "",
        project: t.project?._id || t.project || "",
        department: firstRefId(t.department),
        skill: firstRefId(t.skill),
        readiness: t.readiness || "Ready",
        responsibleSlot: t.responsibleSlot ?? 0,
        budgeted: t.budget?.budgeted ?? "",
        quoted: t.budget?.quoted ?? "",
        checklist: checklistItemsFrom(t),
        projectType: t.projectType || "",
        isActive: t.isActive !== false,
        extraDays: t.extraDays ?? 0,
        daysBeforeStart: t.daysBeforeStart ?? 0,
        quoteLineItems: quoteLineItemsFrom(t),
        assumptions: scopeItemsFrom(t.assumptions),
        inclusions: scopeItemsFrom(t.inclusions),
        exclusions: scopeItemsFrom(t.exclusions),
        tags: (Array.isArray(t.tags) ? t.tags : []).filter((tag) => tag?._id).map((tag) => ({ tag, sourceType: null, sourceId: null })),
    };
}

export default function TemplateEditorWizard({
    open,
    mode,
    templateType,
    initialTemplate,
    onClose,
    onSaved,
}) {
    const dispatch = useDispatch();
    const isEdit = mode === "edit";
    const [activeStep, setActiveStep] = useState(0);
    const [attachments, setAttachments] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [groupTemplates, setGroupTemplates] = useState([]);
    const [projectTemplates, setProjectTemplates] = useState([]);
    const [departments, setDepartments] = useState([]);
    const skills = useSelector((state) => state.skills);
    const [payments, setPayments] = useState([]);

    const [importAccounts, setImportAccounts] = useState([]);
    const [importSources, setImportSources] = useState([]);
    const [importAccountId, setImportAccountId] = useState("");
    const [importSourceId, setImportSourceId] = useState("");
    const [importChildren, setImportChildren] = useState(true);
    const [importSummary, setImportSummary] = useState(null);
    const [importedOverview, setImportedOverview] = useState(null);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form, setForm] = useState(blankForm);

    const isChecklistTemplate = templateType === "checklist";
    const isQuoteTemplate = templateType === "quote";
    const showBillingStep = !isChecklistTemplate && !isQuoteTemplate && templateType !== "task";
    const showAssignStep = !isChecklistTemplate && !isQuoteTemplate && templateType !== "group";
    const showChecklistStep = isChecklistTemplate || templateType === "task";
    const showAttachmentsStep = !isChecklistTemplate && !isQuoteTemplate && templateType !== "group";
    const showImport = !isEdit && (templateType === "group" || templateType === "project");

    const steps = useMemo(() => {
        if (isChecklistTemplate) {
            return [
                { label: "Basics", key: "checklistBasics" },
                { label: "Checklist", key: "checklist" },
            ];
        }
        if (isQuoteTemplate) {
            return [
                { label: "Basics", key: "quoteBasics" },
                { label: "Line Items", key: "quoteLineItems" },
                { label: "Scope", key: "quoteScope" },
            ];
        }
        const s = [
            { label: "Basics", key: "basics" },
            { label: "Classification", key: "classification" },
        ];
        if (showChecklistStep) {
            s.push({ label: "Checklist", key: "checklist" });
        }
        if (showAssignStep) {
            s.push({ label: "Assign", key: "assign" });
        }
        s.push({ label: "Scheduling", key: "scheduling" });
        s.push({ label: showBillingStep ? "Billing" : "Budget", key: "billing" });
        if (showAttachmentsStep) {
            s.push({ label: "Attachments", key: "attachments" });
        }
        return s;
    }, [isChecklistTemplate, isQuoteTemplate, showBillingStep, showAssignStep, showChecklistStep, showAttachmentsStep]);

    useEffect(() => {
        if (!open || isChecklistTemplate) return;
        dispatch(getSkills());
    }, [open, isChecklistTemplate, dispatch]);

    useEffect(() => {
        if (!open) return;
        setActiveStep(0);
        if (isChecklistTemplate || isQuoteTemplate) return;
        dispatch(getTemplates("group", 0, 200, (data) => {
            setGroupTemplates((data || []).filter((t) => t.templateType === "group"));
        }));
        dispatch(getTemplates("project", 0, 500, (data) => {
            setProjectTemplates((data || []).filter((t) => t.templateType === "project"));
        }));
        dispatch(getDepartments(0, 100, (result) => setDepartments(result || [])));
    }, [open, isChecklistTemplate, isQuoteTemplate, dispatch]);

    useEffect(() => {
        if (!open) return;
        if (!initialTemplate) {
            setForm(isQuoteTemplate ? { ...blankForm(), billingType: "Fixed Price" } : blankForm());
            setPayments([]);
            setAttachments([]);
            setExistingAttachments([]);
            return;
        }

        setForm((prev) => ({ ...prev, ...formFromTemplate(initialTemplate) }));

        if (Array.isArray(initialTemplate.payments)) {
            setPayments(
                initialTemplate.payments.map((p) => ({
                    paymentNumber: p.paymentNumber,
                    value: p.value || "",
                    date: p.date ? formatDate(p.date) : "",
                }))
            );
        } else {
            setPayments([]);
        }
        setAttachments([]);
        setExistingAttachments(
            Array.isArray(initialTemplate.attachments)
                ? initialTemplate.attachments.map((a) => ({ name: a.name, size: a.size, mimeType: a.mimeType, storageProvider: a.storageProvider, storageKey: a.storageKey }))
                : []
        );
    }, [open, initialTemplate]);

    useEffect(() => {
        if (!open) return;
        setImportAccountId("");
        setImportSourceId("");
        setImportChildren(true);
        setImportSummary(null);
        setImportSources([]);
        setImportedOverview(null);
    }, [open]);

    useEffect(() => {
        if (!open || !showImport) return;
        dispatch(getAccounts(0, 0, null, false, (data) => setImportAccounts(data || [])));
    }, [open, showImport, dispatch]);

    useEffect(() => {
        if (!open || !showImport) return;
        setImportSourceId("");
        setImportSummary(null);
        setImportSources([]);
        setImportedOverview(null);
        setForm(blankForm());
        setPayments([]);
        if (!importAccountId) return;
        const cb = (data) => setImportSources(data || []);
        if (templateType === "group") {
            dispatch(getAccountGroups(importAccountId, cb));
        } else {
            dispatch(getAccountProjects(importAccountId, "", cb));
        }
    }, [importAccountId, open, showImport, templateType, dispatch]);

    const handleImportSourcePick = (sourceId) => {
        setImportSourceId(sourceId || "");
        setImportSummary(null);
        setImportedOverview(null);
        if (!sourceId) {
            setForm(blankForm());
            setPayments([]);
            return;
        }
        dispatch(getTemplateImportSource(templateType, sourceId, (data) => {
            if (!data) return;
            setForm((prev) => ({ ...prev, ...formFromTemplate(data.template) }));
            setPayments(
                Array.isArray(data.template.payments)
                    ? data.template.payments.map((p) => ({ paymentNumber: p.paymentNumber, value: p.value || "", date: p.date ? formatDate(p.date) : "" }))
                    : []
            );
            setImportedOverview(data.template.overview || "");
            setImportSummary({ projects: data.childProjectCount, tasks: data.childTaskCount, files: data.attachmentCount });
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (name === "billingType" && value !== "Fixed Price") {
            setPayments([]);
        }
    };

    const handleFormUpdate = (updates) => {
        setForm((prev) => ({ ...prev, ...updates }));
    };

    const handleRemoveExistingAttachment = (storageKey) => setExistingAttachments((prev) => prev.filter((a) => a.storageKey !== storageKey));

    const hasChecklistSteps = (form.checklist || []).some((item) => (item.name || "").trim().length > 0);
    const quoteLineItems = form.quoteLineItems || [];
    const hasQuoteLineItems = quoteLineItems.length > 0;
    const everyQuoteLineItemTitled = quoteLineItems.every((i) => (i.title || "").trim().length > 0);
    const quoteLineItemsValid = hasQuoteLineItems && everyQuoteLineItemTitled;
    const quoteBasicsValid = form.name.trim().length > 0 && (form.description || "").trim().length > 0;

    const validateStepByIndex = (stepIndex) => {
        switch (steps[stepIndex]?.key) {
            case "checklistBasics":
                return form.name.trim().length > 0;
            case "quoteBasics":
                return quoteBasicsValid;
            case "quoteLineItems":
                return quoteLineItemsValid;
            case "basics":
                return form.name.trim().length > 0;
            case "classification":
                if (templateType === "task" && !form.project) return false;
                return true;
            case "checklist":
                // A task template may legitimately carry no checklist; a checklist template is nothing but its steps.
                return !isChecklistTemplate || hasChecklistSteps;
            default:
                return true;
        }
    };

    const validateStep = () => validateStepByIndex(activeStep);

    const canNavigateToStep = (targetIndex) => {
        for (let i = 0; i < targetIndex; i++) {
            if (!validateStepByIndex(i)) return false;
        }
        return true;
    };

    const handleStepClick = (stepIndex) => {
        if (stepIndex === activeStep) return;
        if (canNavigateToStep(stepIndex)) {
            setActiveStep(stepIndex);
        }
    };

    const handleNext = () => {
        if (activeStep < steps.length - 1) {
            setActiveStep((prev) => prev + 1);
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep((prev) => prev - 1);
        }
    };

    const buildChecklistPayload = () => ({
        name: form.name.trim(),
        description: form.description || "",
        items: (form.checklist || []).map((i) => (i.name || "").trim()).filter(Boolean).map((name) => ({ name })),
    });

    const buildQuoteTemplatePayload = () => {
        if (!quoteLineItems.length) {
            throw new Error("Add at least one line item before saving this template.");
        }
        const untitled = quoteLineItems.findIndex((i) => !(i.title || "").trim());
        if (untitled !== -1) {
            throw new Error(`Line item ${untitled + 1} has no title. Give it one, or remove the line.`);
        }
        const toStrings = (items) => (items || []).map((i) => (i.name || "").trim()).filter(Boolean);
        return {
            name: form.name.trim(),
            description: (form.description || "").trim(),
            projectType: form.projectType || "",
            billingType: form.billingType || "Fixed Price",
            skill: form.skill || null,
            daysBeforeStart: Math.max(0, Number(form.daysBeforeStart) || 0),
            extraDays: Math.max(0, Number(form.extraDays) || 0),
            isActive: form.isActive !== false,
            lineItems: quoteLineItems.map((i) => ({
                title: (i.title || "").trim(),
                description: i.description || "",
                workType: i.workType || null,
                hours: Number(i.hours) || 0,
                isActionable: !!i.isActionable,
            })),
            assumptions: toStrings(form.assumptions),
            inclusions: toStrings(form.inclusions),
            exclusions: toStrings(form.exclusions),
        };
    };

    const buildPayload = () => {
        const payload = {
            displayName: form.displayName || null,
            name: form.name,
            overview: form.overview,
            priority: form.priority,
            privacy: form.privacy,
            tags: (form.tags || []).map((entry) => entry.tag._id),
            templateDateType: form.templateDateType,
            templateStartOffset: Number(form.templateStartOffset) || 0,
            templateDueOffset: Number(form.templateDueOffset) || 0,
            startDate: form.startDate ? new Date(form.startDate) : null,
            dueDate: form.dueDate ? new Date(form.dueDate) : null,
            attachments: [
                ...existingAttachments.map((a) => ({ name: a.name, size: a.size, mimeType: a.mimeType, storageProvider: a.storageProvider, storageKey: a.storageKey, existing: true })),
                ...restoreAttachments(attachments),
            ],
        };

        if (templateType === "task") {
            payload.billingType = form.billingType;
        } else {
            payload.billingTerms = { ...(initialTemplate?.billingTerms || {}), type: form.billingType };
        }

        if (templateType === "project") {
            payload.status = form.status;
            payload.group = form.group || null;
        }

        if (templateType === "task") {
            payload.project = form.project;
            payload.readiness = form.readiness;
        }

        payload.department = templateType === "task" ? (form.department ? [form.department] : []) : form.department || null;
        if (templateType !== "group") {
            payload.skill = form.skill || null;
        }

        const checklistItems = (form.checklist || []).map((i) => (i.name || "").trim()).filter(Boolean);
        if (templateType === "task") {
            payload.checklist = checklistItems.length
                ? { name: "Checklist", items: checklistItems.map((name, index) => ({ name, order: index })) }
                : null;
        }

        if (templateType !== "group") {
            payload.responsibleSlot = form.responsibleSlot || null;
        }

        const toHours = (v) => (v === "" || v === null || v === undefined ? undefined : Number(v));
        payload.budget = { budgeted: toHours(form.budgeted), quoted: toHours(form.quoted) };

        const showPayments = templateType !== "task" && form.billingType === "Fixed Price";
        if (showPayments && payments.length > 0) {
            const invalid = payments.filter((p) => !p.value || parseFloat(p.value) <= 0);
            if (invalid.length > 0) {
                throw new Error("All payments must have a value greater than $0.00");
            }
            const total = payments.reduce((acc, p) => acc + (parseFloat(p.value) || 0), 0);
            payload.payments = payments.map((p) => ({
                paymentNumber: p.paymentNumber,
                status: "Pending",
                percentage: total > 0 ? ((parseFloat(p.value) || 0) / total) * 100 : 0,
                value: parseFloat(p.value) || 0,
                date: p.date ? new Date(p.date) : null,
            }));
            payload.totalPayment = total;
        }

        if (payload.templateDateType === "relative") {
            payload.templateStartRelative = form.templateStartRelative || null;
            payload.templateDueRelative = form.templateDueRelative || null;
            payload.startDate = null;
            payload.dueDate = null;
        } else if (payload.templateDateType === "offset") {
            payload.startDate = null;
            payload.dueDate = null;
            payload.templateStartRelative = null;
            payload.templateDueRelative = null;
        } else {
            payload.templateStartOffset = 0;
            payload.templateDueOffset = 0;
            payload.templateStartRelative = null;
            payload.templateDueRelative = null;
        }

        return payload;
    };

    const handleSubmit = () => {
        try {
            const payload = isChecklistTemplate ? buildChecklistPayload() : isQuoteTemplate ? buildQuoteTemplatePayload() : buildPayload();
            setIsSubmitting(true);
            const done = (response) => {
                setIsSubmitting(false);
                if (response && onSaved) onSaved(response);
            };
            if (isChecklistTemplate) {
                isEdit ? dispatch(updateChecklistTemplate(initialTemplate._id, payload, done)) : dispatch(createChecklistTemplate(payload, done));
                return;
            }
            if (isQuoteTemplate) {
                isEdit ? dispatch(updateQuoteTemplate(initialTemplate._id, payload, done)) : dispatch(createQuoteTemplate(payload, done));
                return;
            }
            if (showImport && importSourceId) {
                dispatch(importTemplate({ sourceType: templateType, sourceId: importSourceId, template: payload, importChildren }, done));
                return;
            }
            if (templateType === "group") {
                isEdit ? dispatch(updateGroupTemplate(initialTemplate._id, payload, done)) : dispatch(createGroupTemplate(payload, done));
            } else if (templateType === "project") {
                isEdit ? dispatch(updateProjectTemplate(initialTemplate._id, payload, done)) : dispatch(createProjectTemplate(payload, done));
            } else if (templateType === "task") {
                isEdit ? dispatch(updateTaskTemplate(initialTemplate._id, payload, done)) : dispatch(createTaskTemplate(payload, done));
            }
        } catch (e) {
            dispatch(showAlert({ severity: "error", message: e.message || "Failed to save template" }));
        }
    };

    const isLastStep = activeStep === steps.length - 1;
    const title = `${isEdit ? "Edit" : "Create"} ${templateType} template`;

    const editorInitialOverview = useMemo(() => {
        if (importedOverview != null) return importedOverview || null;
        if (!initialTemplate) return null;
        return initialTemplate.overview || null;
    }, [initialTemplate?._id, importedOverview]);

    const renderStepContent = () => {
        switch (steps[activeStep].key) {
            case "checklistBasics":
                return <StepChecklistBasics form={form} onChange={handleChange} />;
            case "quoteBasics":
                return <StepQuoteBasics form={form} onChange={handleFormUpdate} skills={skills} />;
            case "quoteLineItems":
                return <StepQuoteLineItems items={form.quoteLineItems} onChange={(items) => handleFormUpdate({ quoteLineItems: items })} skills={skills} />;
            case "quoteScope":
                return <StepQuoteScope form={form} onChange={handleFormUpdate} />;
            case "basics":
                return (
                    <StepBasics
                        form={form}
                        onChange={handleChange}
                        onOverviewChange={(content) => handleFormUpdate({ overview: content })}
                        onTagsChange={(tags) => handleFormUpdate({ tags })}
                        editorInitialOverview={editorInitialOverview}
                        mode={mode}
                        templateType={templateType}
                        initialTemplate={initialTemplate}
                        overviewKey={importSourceId ? `${importSourceId}-${importedOverview == null ? "loading" : "loaded"}` : ""}
                        showImport={showImport}
                        importAccounts={importAccounts}
                        importAccountId={importAccountId}
                        onImportAccountChange={setImportAccountId}
                        importSources={importSources}
                        importSourceId={importSourceId}
                        onImportSourcePick={handleImportSourcePick}
                        importChildren={importChildren}
                        onImportChildrenChange={setImportChildren}
                        importSummary={importSummary}
                    />
                );
            case "classification":
                return (
                    <StepClassification
                        form={form}
                        onChange={handleChange}
                        templateType={templateType}
                        groupTemplates={groupTemplates}
                        projectTemplates={projectTemplates}
                        departments={departments}
                        skills={skills}
                    />
                );
            case "checklist":
                return (
                    <StepChecklist
                        items={form.checklist}
                        onChange={(items) => handleFormUpdate({ checklist: items })}
                        helperText={isChecklistTemplate ? "These steps are copied onto a task's checklist each time this template is imported." : undefined}
                        emptyText={isChecklistTemplate ? "Add at least one step. A checklist template cannot be saved without one." : undefined}
                    />
                );
            case "assign":
                return <StepAssign form={form} onChange={handleChange} />;
            case "scheduling":
                return <StepScheduling form={form} onChange={handleChange} />;
            case "billing":
                return (
                    <StepBilling
                        form={form}
                        onChange={handleChange}
                        payments={payments}
                        setPayments={setPayments}
                        templateType={templateType}
                    />
                );
            case "attachments":
                return (
                    <StepAttachments
                        setAttachments={setAttachments}
                        onUploadStatusChange={setIsUploading}
                        existingAttachments={existingAttachments}
                        onRemoveExisting={handleRemoveExistingAttachment}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <StyledDialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                {title}
                <IconButton
                    onClick={onClose}
                    size="small"
                    aria-label="close"
                    style={{ position: "absolute", right: 12, top: 12 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((step, index) => (
                        <Step
                            key={step.key}
                            completed={index < activeStep}
                            style={{ cursor: canNavigateToStep(index) ? "pointer" : "default" }}
                            onClick={() => handleStepClick(index)}
                        >
                            <StepLabel>{step.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <StepContent>{renderStepContent()}</StepContent>

                <WizardActions direction="row" justifyContent="space-between">
                    <Button onClick={activeStep === 0 ? onClose : handleBack}>
                        {activeStep === 0 ? "Cancel" : "Back"}
                    </Button>
                    <Stack direction="row" spacing={1}>
                        {!isLastStep && (
                            <Button variant="contained" onClick={handleNext} disabled={!validateStep()}>
                                Next
                            </Button>
                        )}
                        {isLastStep && (
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={
                                    !form.name.trim() ||
                                    isUploading ||
                                    isSubmitting ||
                                    (templateType === "task" && !form.project) ||
                                    (isChecklistTemplate && !hasChecklistSteps) ||
                                    (isQuoteTemplate && (!quoteBasicsValid || !quoteLineItemsValid))
                                }
                            >
                                {isEdit ? "Save Changes" : "Create Template"}
                            </Button>
                        )}
                    </Stack>
                </WizardActions>
            </DialogContent>
        </StyledDialog>
    );
}
